import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { BookOpen, CircleHelp } from 'lucide-react';
import { Button, cn } from '@/ui/core';
import { Dialog } from '@/ui/overlay';
import { topicById } from './topics';

/**
 * A round "?" next to a feature. Tapping it explains the feature in a few steps, with a
 * button to the full guide. The visible circle is small; the tap area is a full 44px.
 */
export function HelpBubble({ topic, className, size = 'md', tone = 'default' }: { topic: string; className?: string; size?: 'sm' | 'md'; tone?: 'default' | 'onAccent' }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const t = topicById.get(topic);
  if (!t) return null;
  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          // Bubbles sit beside buttons and links; never trigger those too.
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={`Help: ${t.title}`}
        className={cn('inline-flex size-11 shrink-0 items-center justify-center rounded-full align-middle no-print', className)}
      >
        <span
          aria-hidden
          className={cn(
            'flex items-center justify-center rounded-full border-2 font-bold leading-none',
            size === 'sm' ? 'size-6 text-sm' : 'size-7 text-base',
            tone === 'onAccent' ? 'border-current bg-black/15 text-current' : 'border-accent/60 bg-surface text-accent',
          )}
        >
          ?
        </span>
      </button>
      {/* Rendered at the top of the page: the bubble often sits inside a heading or a sentence,
          and a tap inside the window must never reach the button or link it sits next to. */}
      {open &&
        createPortal(
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={
          <span className="flex items-center gap-2">
            <CircleHelp className="size-5 text-accent" aria-hidden /> {t.title}
          </span>
        }
        footer={
          <>
            <Button size="lg" onClick={() => setOpen(false)}>
              Got it
            </Button>
            <Button
              size="lg"
              variant="primary"
              icon={<BookOpen className="size-5" aria-hidden />}
              onClick={() => {
                setOpen(false);
                navigate(`/help/${t.id}`);
              }}
            >
              More in the guide
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 text-base leading-relaxed">
          <p className="text-lg">{t.short}</p>
          <ol className="flex flex-col gap-2">
            {t.steps.map((s, i) => (
              <li key={s} className="flex gap-3">
                <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-bold text-text" aria-hidden>
                  {i + 1}
                </span>
                <span className="pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
          {t.tips?.[0] && <p className="rounded-xl bg-surface-2 p-3 text-sm">{t.tips[0]}</p>}
        </div>
      </Dialog>
          </div>,
          document.body,
        )}
    </>
  );
}
